'use client';

import React, { useState, useRef } from 'react';
import { XMarkIcon, CloudArrowUpIcon, DocumentIcon } from '@heroicons/react/24/outline';

interface FileUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (files: File[]) => void;
    /** Allowed file extensions (without dot). Example: ['jpg','png','pdf'] */
    allowedExtensions?: string[];
}

export default function FileUploadModal({ isOpen, onClose, onUpload, allowedExtensions }: FileUploadModalProps) {
    const [dragActive, setDragActive] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [invalidFiles, setInvalidFiles] = useState<{ name: string; reason: string }[]>([]);

    if (!isOpen) return null;

    const handleCloseModal = () => {
        setSelectedFiles([]);
        setInvalidFiles([]);
        onClose();
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const files = Array.from(e.dataTransfer.files);
            addFiles(files);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const files = Array.from(e.target.files);
            addFiles(files);
            // allow re-selecting same file
            e.currentTarget.value = '';
        }
    };

    const removeFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // ---------------- Validation logic ----------------
    const defaultAllowed = [
        // Images
        'jpg','jpeg','png','webp','gif','bmp','tiff',
        // Video
        'mp4','mov','avi','mkv',
        // Audio
        'mp3','wav','aac',
        // Documents
        'pdf','txt','csv','xlsx','xls','docx','pptx',
        // Design
        'psd','ai','eps',
        // 3D
        'glb','gltf','obj','stl','ply','3mf'
    ];

    const allowedExtSet = new Set((allowedExtensions || defaultAllowed).map(e => e.toLowerCase()));

    const getExtension = (name: string) => {
        const parts = name.split('.');
        if (parts.length <= 1) return '';
        return parts.pop()!.toLowerCase();
    };

    const isAllowedExtension = (name: string) => {
        const ext = getExtension(name);
        return !!ext && allowedExtSet.has(ext);
    };

    const buildAccept = (exts: string[]) => exts.map(e => '.' + e).join(',');

    const addFiles = (files: File[]) => {
        const valid: File[] = [];
        const invalid: { name: string; reason: string }[] = [];

        const curFiles = [...selectedFiles];

        for (const file of files) {
            if (!isAllowedExtension(file.name)) {
                invalid.push({ name: file.name, reason: 'Loại file không được hỗ trợ' });
                continue;
            }

            // Duplicate check (name+size) to avoid adding same file twice
            const duplicate = curFiles.some(f => f.name === file.name && f.size === file.size);
            if (duplicate) {
                invalid.push({ name: file.name, reason: 'File đã tồn tại' });
                continue;
            }

            valid.push(file);
        }

        if (valid.length > 0) setSelectedFiles(prev => [...prev, ...valid]);
        if (invalid.length > 0) setInvalidFiles(prev => [...prev, ...invalid]);
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;
        
        setUploading(true);
        try {
            await onUpload(selectedFiles);
            setSelectedFiles([]);
            setInvalidFiles([]);
            onClose();
        } catch (error) {
            console.error('Upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    const formatFileSize = (bytes: number): string => {
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div className="fixed inset-0 bg-[#00000099] flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">Upload File</h3>
                    <button
                        onClick={handleCloseModal}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 overflow-y-auto max-h-[400px]">
                    {/* Drag & Drop Area */}
                    <div
                        className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors ${
                            dragActive
                                ? 'border-blue-400 bg-blue-50'
                                : 'border-gray-300 hover:border-gray-400'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <CloudArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-lg font-medium text-gray-700 mb-2">
                            Kéo thả file vào đây hoặc click để chọn
                        </p>
                        <p className="text-sm text-gray-500 w-4/5 mx-auto break-words">
                            Hỗ trợ: {buildAccept(Array.from(allowedExtSet))}
                        </p>
                        <p className="text-sm text-gray-500 w-4/5 mx-auto break-words">
                            Khuyến nghị dưới 1MB
                        </p>
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={buildAccept(Array.from(allowedExtSet))}
                        onChange={handleFileSelect}
                        className="hidden"
                    />

                    {/* Selected Files List */}
                    {selectedFiles.length > 0 && (
                        <div className="mt-6">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">
                                File đã chọn ({selectedFiles.length})
                            </h4>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                                {selectedFiles.map((file, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                    >
                                        <div className="flex items-center gap-3">
                                            <DocumentIcon className="w-5 h-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {file.name}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {formatFileSize(file.size)}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeFile(index);
                                            }}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <XMarkIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Invalid files */}
                    {invalidFiles.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                            <h4 className="text-sm font-medium text-red-700 mb-2">File không hợp lệ</h4>
                            <ul className="text-sm text-red-700 list-disc list-inside">
                                {invalidFiles.map((it, idx) => (
                                    <li key={idx} className="truncate">{it.name}: {it.reason}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
                    <button
                        onClick={handleCloseModal}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        disabled={uploading}
                    >
                        Hủy
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={selectedFiles.length === 0 || uploading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {uploading && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        )}
                        {uploading ? 'Đang upload...' : `Upload${selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
